import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { v4 as uuidv4 } from 'uuid';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CartContext = createContext(null);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const { token, isAuthenticated } = useAuth();
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => {
    const stored = localStorage.getItem('sessionId');
    if (stored) return stored;
    const newId = uuidv4();
    localStorage.setItem('sessionId', newId);
    return newId;
  });

  useEffect(() => {
    fetchCart();
  }, [isAuthenticated, token]);

  const fetchCart = async () => {
    setLoading(true);
    try {
      if (isAuthenticated && token) {
        const response = await axios.get(`${API}/cart`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCart(response.data);
      } else {
        const response = await axios.get(`${API}/guest-cart/${sessionId}`);
        setCart(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch cart:', error);
      setCart([]);
    } finally {
      setLoading(false);
    }
  };

  const addToCart = async (productId, quantity, size, color) => {
    try {
      if (isAuthenticated && token) {
        await axios.post(
          `${API}/cart/add`,
          { product_id: productId, quantity, size, color },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        await axios.post(`${API}/guest-cart/add?session_id=${sessionId}`, {
          product_id: productId,
          quantity,
          size,
          color
        });
      }
      await fetchCart();
      return true;
    } catch (error) {
      console.error('Failed to add to cart:', error);
      return false;
    }
  };

  const updateQuantity = async (productId, quantity, size, color) => {
    try {
      if (isAuthenticated && token) {
        await axios.put(
          `${API}/cart/update`,
          { product_id: productId, quantity, size, color },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        await fetchCart();
      }
    } catch (error) {
      console.error('Failed to update cart:', error);
    }
  };

  const removeFromCart = async (productId, size, color) => {
    try {
      if (isAuthenticated && token) {
        await axios.delete(
          `${API}/cart/remove/${productId}?size=${encodeURIComponent(size)}&color=${encodeURIComponent(color)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        await fetchCart();
      }
    } catch (error) {
      console.error('Failed to remove from cart:', error);
    }
  };

  const clearCart = async () => {
    try {
      if (isAuthenticated && token) {
        await axios.delete(`${API}/cart/clear`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCart([]);
      }
    } catch (error) {
      console.error('Failed to clear cart:', error);
    }
  };

  const cartTotal = cart.reduce((total, item) => {
    const price = item.product?.price || 0;
    return total + price * item.quantity;
  }, 0);

  const cartCount = cart.reduce((count, item) => count + item.quantity, 0);

  const value = {
    cart,
    loading,
    sessionId,
    addToCart,
    updateQuantity,
    removeFromCart,
    clearCart,
    fetchCart,
    cartTotal,
    cartCount
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};
